import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { fetchCategories } from '../../utils/api';

const ACCENT_STYLES = [
  { border: 'border-accent1/20 hover:border-accent1/50', bg: 'from-accent1/10 to-accent1/5',   emoji: '🧸' },
  { border: 'border-accent2/20 hover:border-accent2/50', bg: 'from-accent2/10 to-accent2/5',   emoji: '🚗' },
  { border: 'border-secondary/20 hover:border-secondary/50', bg: 'from-secondary/10 to-secondary/5', emoji: '🚲' },
  { border: 'border-accent3/20 hover:border-accent3/50', bg: 'from-accent3/10 to-accent3/5',   emoji: '🎨' },
  { border: 'border-primary/20 hover:border-primary/50', bg: 'from-primary/10 to-primary/5',   emoji: '🧩' },
  { border: 'border-accent2/20 hover:border-accent2/50', bg: 'from-accent2/10 to-accent2/5',   emoji: '📚' },
];

export default function CategoryGrid({
  title       = 'Shop by Category',
  subtitle    = 'Find the perfect gift for your little one! 🎁',
  columns     = 6,
  categoryIds = [],
}) {
  const selectedIds = useMemo(
    () => (Array.isArray(categoryIds) ? categoryIds.map(String).filter(Boolean) : []),
    [categoryIds],
  );

  const { data: allCategories = [], isLoading, isError } = useQuery({
    queryKey: ['product-categories'],
    queryFn:  () => fetchCategories({ per_page: 100, hide_empty: false }),
    staleTime: 5 * 60_000,
  });

  const categories = useMemo(() => {
    if (selectedIds.length > 0) {
      return allCategories.filter((cat) => selectedIds.includes(String(cat.id)));
    }
    return allCategories;
  }, [allCategories, selectedIds]);

  const siteUrl = window.themeData?.siteUrl ?? '';
  const colClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
  }[columns] ?? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6';

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-textDark mb-4">{title}</h2>
          {subtitle && <p className="text-xl text-textLight">{subtitle}</p>}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20 gap-3 text-primary">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="font-semibold text-textLight">Loading categories…</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-16 gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Could not load categories. Check WooCommerce is active.</span>
          </div>
        )}

        {!isLoading && !isError && (
          <div className={`grid ${colClass} gap-6`}>
            {categories.map((cat, i) => {
              const style = ACCENT_STYLES[i % ACCENT_STYLES.length];
              const href  = `${siteUrl}/product-category/${cat.slug}/`;
              const image = cat.image?.src;

              return (
                <a key={cat.id} href={href} className="group">
                  <div className={`bg-gradient-to-br ${style.bg} rounded-3xl p-8 text-center hover:shadow-xl transition-all border-4 ${style.border} transform hover:-translate-y-2`}>
                    {image
                      ? <img
                          src={image}
                          alt={cat.name}
                          className="w-16 h-16 object-cover rounded-2xl mx-auto mb-4 group-hover:scale-110 transition-transform"
                        />
                      : <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
                          {style.emoji}
                        </div>
                    }
                    <h3 className="font-black text-textDark text-lg">{cat.name}</h3>
                    <p className="text-sm text-textLight mt-1">{cat.count} item{cat.count !== 1 ? 's' : ''}</p>
                  </div>
                </a>
              );
            })}

            {!isLoading && categories.length === 0 && (
              <div className="col-span-full text-center py-10 text-textLight">
                <p>No categories found. Add products to WooCommerce first.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
